
import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { Row, Col, Container } from 'react-bootstrap';
import { useLocation } from 'react-router-dom';
import './styles/AdobeDownloadInstructions.scss';
import DownloadInstructionsLayout from 'components/layouts/DownloadInstructionsLayout';
import routes from 'lib/routes';
import useIsMountedRef from 'hooks/useIsMountedRef';
import { api, handleAjaxError, CancelToken } from 'lib/ajax';
import { getDrmTypeEnginesByNameObj, pint } from 'lib/helpers';
import usePageTitle from 'hooks/usePageTitle';
import ProductDownloadInstructions from 'pages/ProductDownloadInstructions';
const drmTypeEnginesByName = getDrmTypeEnginesByNameObj();
let cancelRequestSource;

export default function AdobeDownloadInstructions(props) {
    usePageTitle('Adobe DRM');
    const { state: locState } = useLocation();
    const isFromProductPageCheckout = !!locState && !!locState.fromProductPageCheckout;
    const merchant = (!!locState && !!locState.merchant) ? locState.merchant : null;
    const productDetails = (!!locState && !!locState.productDetails) ? locState.productDetails : null;
    const [trans, setTrans] = useState(null);
    const isMoutedRef = useIsMountedRef();
    const transactionId = (props.match.params && props.match.params.transactionId) ? props.match.params.transactionId : null;
    const { history } = props;

    useEffect(() => {
        async function getBookTransactionDetails() {
            try {
                cancelRequestSource = CancelToken.source();
                const res = await api.httpGet(`${routes.api.publicGetTransDetails}/${transactionId}`, {
                    cancelToken: cancelRequestSource.token
                });
                if (isMoutedRef.current) {
                    setTrans(res.data);
                }
            }
            catch (err) {
                handleAjaxError(err);
                requestAnimationFrame(() => history.push(routes.home));    
            }
        }
        transactionId && getBookTransactionDetails();
        return () => {
            if (typeof cancelRequestSource !== 'undefined') {
                cancelRequestSource.cancel();
            }
        };
    }, [isMoutedRef, transactionId, history]);

    if (!transactionId) {
        requestAnimationFrame(() => props.history.push(routes.home));
        return null;
    }

    if (trans) { // make sure the transaction is adobe
        if (pint(trans['drm']) !== drmTypeEnginesByName['adobeHardened'] && pint(trans['drm']) !== drmTypeEnginesByName['adobeLegacy']) {
            requestAnimationFrame(() => props.history.push(routes.home));
            return null;
        }
    }
    else {
        return (<div className="p-3">Loading...</div>);
    }
    let branding = {
            primary: '#293D79',
            secondary: '#6A98EF',
            imageURL: '/images/logo.png',
            showPoweredBy: true
    };
    if (trans && !!trans.brand) { // branding is set
        branding = {
            primary: trans.brand['primary_color'],
            secondary: trans.brand['secondary_color'],
            imageURL: trans.brand['logo_url'],
            showPoweredBy: trans.brand['show_powered_label']
        };
    }
    let secondaryBackgroundColorStyle = {
        background: branding.secondary
    },
    primaryColorStyle = {
        color: branding.primary
    },
    secondaryryColorStyle = {
        color: branding.secondary
    };
    
    return (
        <>
            {
                isFromProductPageCheckout ?
                    <ProductDownloadInstructions 
                        merchant={merchant} 
                        title={(!trans) ? 'Loading' : trans['ebook_title']} 
                        downloadLink={`${routes.api.fulFillmentDownloadLink}/${trans['id']}`} 
                        productDetails={productDetails}
                    >
                        <DownloadInstructionsLayout.InstructionList>
                            <li>
                                <div style={secondaryBackgroundColorStyle} className="num" data-num="1"></div>
                                <div className="num-cont">
                                    <h4 style={primaryColorStyle}>Create your free Adobe account <a style={secondaryryColorStyle} href={routes.outside.createAdobeAccount} target="_blank" rel="noreferrer">here</a></h4>
                                    <p style={{...primaryColorStyle, opacity: 0.75}}>This will create a free account you can use to access your books.</p>
                                </div>
                            </li>
                            <li>
                                <div style={secondaryBackgroundColorStyle} className="num" data-num="2"></div>
                                <div className="num-cont">
                                    <h4 style={primaryColorStyle}>Install <a style={secondaryryColorStyle} href={routes.outside.adobeDigitalEditionsDownload} target="_blank" rel="noreferrer">Adobe Digital Editions</a> for your device</h4>
                                    <p style={{...primaryColorStyle, opacity: 0.75}}>You will need to use the Adobe Digital Editions reader app to view your books.</p>
                                </div>
                            </li>
                            <li>
                                <div style={secondaryBackgroundColorStyle} className="num" data-num="3"></div>
                                <div className="num-cont">
                                    <h4 style={primaryColorStyle}>Download your book access file <a style={secondaryryColorStyle} href={`${routes.api.fulFillmentDownloadLink}/${trans['id']}`}>here</a></h4>
                                    <p style={{...primaryColorStyle, opacity: 0.75}}>Open your ACSM file with Adobe Digital Editions and enjoy your book!</p>
                                </div>
                            </li>
                        </DownloadInstructionsLayout.InstructionList>
                    </ProductDownloadInstructions> 
                    :
                    <DownloadInstructionsLayout mode={(trans && !!trans.brand) ? 'custom' : ''} branding={branding}>
                        <DownloadInstructionsLayout.MainContainer 
                            title={(!trans) ? 'Loading' : trans['ebook_title']} 
                            mode={(trans && !!trans.brand) ? 'custom' : ''} 
                            branding={branding}
                        >
                            {(() => {
                                if (!trans) {
                                    return (
                                        <div className="p-5 m-5 text-center">Loading instructions...</div>
                                    );
                                }
                                else {
                                    return (
                                        <DownloadInstructionsLayout.InstructionList>
                                            <li>
                                                <div style={secondaryBackgroundColorStyle} className="num" data-num="1"></div>
                                                <div className="num-cont">
                                                    <h4 style={primaryColorStyle}>Create your free Adobe account <a style={secondaryryColorStyle} href={routes.outside.createAdobeAccount} target="_blank" rel="noreferrer">here</a></h4>
                                                    <p style={{...primaryColorStyle, opacity: 0.75}}>This will create a free account you can use to access your books.</p>
                                                </div>
                                            </li>
                                            <li>
                                                <div style={secondaryBackgroundColorStyle} className="num" data-num="2"></div>
                                                <div className="num-cont">
                                                    <h4 style={primaryColorStyle}>Install <a style={secondaryryColorStyle} href={routes.outside.adobeDigitalEditionsDownload} target="_blank" rel="noreferrer">Adobe Digital Editions</a> for your device</h4>
                                                    <p style={{...primaryColorStyle, opacity: 0.75}}>You will need to use the Adobe Digital Editions reader app to view your books.</p>
                                                </div>
                                            </li>
                                            <li>
                                                <div style={secondaryBackgroundColorStyle} className="num" data-num="3"></div>
                                                <div className="num-cont">
                                                    <h4 style={primaryColorStyle}>Download your book access file <a style={secondaryryColorStyle} href={`${routes.api.fulFillmentDownloadLink}/${trans['id']}`}>here</a></h4>
                                                    <p style={{...primaryColorStyle, opacity: 0.75}}>Open your ACSM file with Adobe Digital Editions and enjoy your book!</p>
                                                </div>
                                            </li>
                                        </DownloadInstructionsLayout.InstructionList>
                                    );
                                }
                            })()}
                        </DownloadInstructionsLayout.MainContainer>
                    </DownloadInstructionsLayout>
            }
        </>
        
    );
}
AdobeDownloadInstructions.propTypes = {
    match: PropTypes.any,
    history: PropTypes.any
};
